/**
 * Payments + refunds API (api-contract.md §3.4 patient, §6 admin).
 *
 * Patient opens a payment attempt → Razorpay Checkout (real gateway) or the dev "simulate"
 * helper (fake gateway), then reports Checkout's outcome (confirm / fail / cancel); the server
 * verifies with Razorpay and the webhook settles anything still pending. Admin initiates refunds.
 */

import { apiFetch, downloadFile, newIdempotencyKey, rawFormFetch } from "./client";

export interface CheckoutPrefill {
  name?: string | null;
  contact?: string | null;
  email?: string | null;
}

/** PaymentAttemptOut: everything Razorpay Checkout needs, passed straight through. */
export interface PaymentAttempt {
  payment_attempt_id: string;
  order_id: string;
  order_ref: string;
  status: string;
  resumed: boolean;
  gateway: "razorpay" | "fake" | string;
  rzp_order_id: string;
  rzp_key_id: string;
  amount: string;
  amount_paise: number;
  currency: string;
  expires_at: string;
  checkout_name: string;
  checkout_description: string;
  checkout_image?: string | null;
  checkout_timeout_seconds: number;
  prefill: CheckoutPrefill;
}

/** PaymentStatusOut: the outcome of confirm / fail / cancel / status. */
export interface PaymentStatus {
  status: "succeeded" | "pending" | "failed" | "cancelled" | "expired";
  message: string;
  retryable: boolean;
  order_id: string;
  order_ref: string;
  order_status_code: string;
  paid_at: string | null;
  /** The attempt itself; the API sends its id as `id` (PaymentAttemptSummary). */
  attempt: PaymentAttemptSummary;
}

/** Razorpay Checkout's error, as the SDK reported it (all optional; the server truncates). */
export interface PaymentFailure {
  code?: string | null;
  reason?: string | null;
  description?: string | null;
  source?: string | null;
  step?: string | null;
  rzp_payment_id?: string | null;
}

export interface PaymentAttemptSummary {
  id: string;
  status: string;
  amount: string;
  rzp_order_id: string;
  rzp_payment_id: string | null;
  failure_reason: string | null;
  initiated_at: string;
  expires_at: string;
  completed_at: string | null;
}

export interface Refund {
  id: string;
  order_id: string;
  payment_attempt_id: string;
  requested_amount: string;
  refunded_amount: string | null;
  retained_pct_applied: string | null;
  reason: string;
  rzp_refund_id: string | null;
  status: string;
  initiated_at: string;
  processed_at: string | null;
  failure_reason: string | null;
  /** The admin's attached reference document, if any (ORD-16). */
  reference_name?: string | null;
}

// ----- Patient (P-12) -----

export const createPaymentAttempt = (orderId: string): Promise<PaymentAttempt> =>
  apiFetch(`/api/v1/me/orders/${orderId}/payment-attempts`, {
    method: "POST",
    idempotencyKey: newIdempotencyKey(),
  });

export const listPaymentAttempts = (orderId: string): Promise<PaymentAttemptSummary[]> =>
  apiFetch(`/api/v1/me/orders/${orderId}/payment-attempts`);

const attemptPath = (orderId: string, attemptId: string) =>
  `/api/v1/me/orders/${orderId}/payment-attempts/${attemptId}`;

/** Outcome of one attempt; while it is open the server re-checks Razorpay. */
export const getPaymentAttempt = (orderId: string, attemptId: string): Promise<PaymentStatus> =>
  apiFetch(attemptPath(orderId, attemptId));

/** Checkout's success callback — the server verifies the signature before the order counts as paid. */
export const confirmPaymentAttempt = (
  orderId: string,
  attemptId: string,
  body: { rzp_payment_id: string; rzp_order_id: string; rzp_signature: string },
): Promise<PaymentStatus> =>
  apiFetch(`${attemptPath(orderId, attemptId)}/confirm`, { method: "POST", body });

export const failPaymentAttempt = (
  orderId: string,
  attemptId: string,
  body: PaymentFailure,
): Promise<PaymentStatus> => apiFetch(`${attemptPath(orderId, attemptId)}/fail`, { method: "POST", body });

export const cancelPaymentAttempt = (
  orderId: string,
  attemptId: string,
  reason?: string,
): Promise<PaymentStatus> =>
  apiFetch(`${attemptPath(orderId, attemptId)}/cancel`, { method: "POST", body: { reason: reason ?? null } });

/** Dev-only (fake gateway): settles the open attempt the way Checkout would have. */
export const simulatePaymentCapture = (
  orderId: string,
  outcome: "captured" | "failed" | "pending" = "captured",
): Promise<{ status: string }> =>
  apiFetch(`/api/v1/dev/orders/${orderId}/simulate-capture?outcome=${encodeURIComponent(outcome)}`, {
    method: "POST",
  });

// ----- Admin (A-09) -----

export const initiateRefund = (
  orderId: string,
  body: { amount?: string; retained_pct?: string; reason: string },
  idempotencyKey?: string,
): Promise<Refund> =>
  apiFetch(`/api/v1/admin/orders/${orderId}/refunds`, {
    method: "POST",
    body,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });

/** An order found by the reference a patient quotes (ORD-12, any case) or its ID (ADM-11). */
export interface AdminOrderLookup {
  id: string;
  order_id: string;
  status_code: string;
  status_label: string;
  patient_name: string | null;
  patient_phone: string | null;
  final_amount: string;
  paid_at: string | null;
  cancelled_at: string | null;
}

export const lookupOrder = (ref: string): Promise<AdminOrderLookup> =>
  apiFetch(`/api/v1/admin/orders/lookup?ref=${encodeURIComponent(ref)}`);

export const listRefunds = (orderId: string): Promise<Refund[]> =>
  apiFetch(`/api/v1/admin/orders/${orderId}/refunds`);

/** Attach the optional reference document to a refund (D-05); a new one replaces it. */
export function attachRefundReference(refundId: string, file: File): Promise<Refund> {
  const form = new FormData();
  form.append("file", file);
  return rawFormFetch(`/api/v1/admin/refunds/${refundId}/reference`, form, {});
}

export const downloadRefundReference = (refund: Refund): Promise<void> =>
  downloadFile(`/api/v1/admin/refunds/${refund.id}/reference`, refund.reference_name || "reference");

// ----- Admin: patients' cancel-and-refund requests (ORD-16, D-05 / D-14) -----

export type CancelRequestStatus = "open" | "refunded" | "declined";

export interface AdminCancelRequest {
  id: string;
  status: CancelRequestStatus;
  reason: string;
  created_at: string;
  resolved_at: string | null;
  resolution_note: string | null;
  refund_id: string | null;
  order_id: string;
  order_ref: string;
  order_status_code: string;
  order_status_label: string;
  patient_id: string;
  patient_name: string | null;
  patient_phone: string | null;
  amount_paid: string;
  gst_total: string;
  /** The most that may be refunded now: (paid − GST) × the refundable share, less earlier refunds. */
  refund_max: string;
  /** Once refunded: the refund given, and where it stands (initiated | processed | failed). */
  refund_amount?: string | null;
  refund_status?: string | null;
}

export interface PaginatedCancelRequests {
  data: AdminCancelRequest[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

/** The list's narrowing: `q` matches the order reference, the patient's name or phone. */
export interface CancelRequestFilter {
  q?: string;
  patient_id?: string;
}

export interface CancelRequestQuery extends CancelRequestFilter {
  status: CancelRequestStatus | "all";
  /** Unset: open requests oldest first (first come, first served), the rest newest first. */
  sort?: "oldest" | "newest";
  page?: number;
  per_page?: number;
}

function filterParams(filter: CancelRequestFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.q?.trim()) params.set("q", filter.q.trim());
  if (filter.patient_id) params.set("patient_id", filter.patient_id);
  return params;
}

export const listCancelRequests = (query: CancelRequestQuery): Promise<PaginatedCancelRequests> => {
  const params = filterParams(query);
  params.set("status", query.status);
  if (query.sort) params.set("sort", query.sort);
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/admin/cancel-requests?${params.toString()}`);
};

/** How many requests are in each status, with the list's search (the tabs' counts). */
export interface CancelRequestCounts {
  open: number;
  refunded: number;
  declined: number;
  all: number;
}

export const countCancelRequests = (filter: CancelRequestFilter = {}): Promise<CancelRequestCounts> =>
  apiFetch(`/api/v1/admin/cancel-requests/counts?${filterParams(filter).toString()}`);

export interface CancelRequestLine {
  medicine_name: string;
  quantity: number;
  is_available: boolean;
  taxable_value: string;
}

/** Another request by the same patient, on any order. */
export interface CancelRequestBrief {
  id: string;
  status: CancelRequestStatus;
  reason: string;
  created_at: string;
  resolved_at: string | null;
  order_id: string;
  order_ref: string;
}

/** One request with what deciding it needs. */
export interface AdminCancelRequestDetail extends AdminCancelRequest {
  resolved_by_name: string | null;
  order_placed_at: string;
  order_paid_at: string | null;
  order_cancelled_at: string | null;
  rzp_payment_id: string | null;
  medicines_subtotal: string;
  delivery_charge: string;
  lines: CancelRequestLine[];
  /** Every refund on the order, newest first (incl. ones given from "Refund an order"). */
  refunds: Refund[];
  /** The patient's other requests, newest first (a few), and how many there are in all. */
  patient_other_requests: CancelRequestBrief[];
  patient_other_requests_total: number;
}

export const getCancelRequest = (id: string): Promise<AdminCancelRequestDetail> =>
  apiFetch(`/api/v1/admin/cancel-requests/${id}`);

export const refundCancelRequest = (
  id: string,
  body: { amount?: string; note: string },
  idempotencyKey?: string,
): Promise<AdminCancelRequest> =>
  apiFetch(`/api/v1/admin/cancel-requests/${id}/refund`, {
    method: "POST",
    body,
    idempotencyKey: idempotencyKey ?? newIdempotencyKey(),
  });

export const declineCancelRequest = (id: string, note: string): Promise<AdminCancelRequest> =>
  apiFetch(`/api/v1/admin/cancel-requests/${id}/decline`, { method: "POST", body: { note } });
